import { apiFetch } from './http';
import { uploadAvatarFile, type UploadPayload } from './uploads';
import { toProfile, type Profile, type ProfileRow, type UpdateProfileInput } from './types';

export async function fetchProfile(): Promise<Profile> {
  const row = await apiFetch<ProfileRow>('/api/profiles/me');
  return toProfile(row);
}

export async function createProfile(input: { name: string; handle: string }): Promise<Profile> {
  const name = input.name.trim();
  if (name.length < 1 || name.length > 100) throw new Error('Name must be 1-100 characters');

  const handle = input.handle.toLowerCase().trim();
  if (!/^[a-z0-9_]{3,32}$/.test(handle)) throw new Error('Invalid handle format');

  // 409 → ApiError with message 'HANDLE_TAKEN' (server body { error: 'HANDLE_TAKEN' })
  const row = await apiFetch<ProfileRow>('/api/profiles', {
    method: 'POST',
    body: JSON.stringify({ name, handle }),
  });
  return toProfile(row);
}

function toProfileUpdateBody(updates: UpdateProfileInput): Record<string, unknown> {
  return {
    ...(updates.name !== undefined ? { name: updates.name } : {}),
    ...(updates.bio !== undefined ? { bio: updates.bio } : {}),
    ...(updates.website !== undefined ? { website: updates.website } : {}),
    ...(updates.websiteUrl !== undefined ? { websiteUrl: updates.websiteUrl } : {}),
    ...(updates.avatarPath !== undefined ? { avatarPath: updates.avatarPath } : {}),
    ...(updates.website_url !== undefined ? { websiteUrl: updates.website_url } : {}),
    ...(updates.avatar_path !== undefined ? { avatarPath: updates.avatar_path } : {}),
  };
}

// `id` is retained for signature compatibility — the server updates the
// authenticated user's own profile.
export async function updateProfile(_id: string, updates: UpdateProfileInput): Promise<Profile> {
  const row = await apiFetch<ProfileRow>('/api/profiles/me', {
    method: 'PATCH',
    body: JSON.stringify(toProfileUpdateBody(updates)),
  });
  return toProfile(row);
}

export async function uploadAvatar(file: UploadPayload | Blob): Promise<string> {
  return uploadAvatarFile(file);
}
